var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { CustomElement, P, PacemEventTarget, Watch, PropertyConverters, Utils, LogLevel } from './_default-imports';
import { Fetcher, HttpMethod, FetchSuccessEventName, FetchErrorEventName, FetchResultEventName } from '../../net/fetcher';
//namespace Pacem.Components {
const ABORT_MESSAGE = "New incoming request overrides the ongoing one. Aborting.";
let PacemFetchElement = class PacemFetchElement extends PacemEventTarget {
    constructor() {
        super(...arguments);
        /** Gets or sets whether to trigger a fetch whenever a significant property has changed (default: true). */
        this.autofetch = true;
        this.debounce = 100;
    }
    viewActivatedCallback() {
        super.viewActivatedCallback();
        this._delayAndConditionallyFetch();
    }
    propertyChangedCallback(name, old, val, first) {
        super.propertyChangedCallback(name, old, val, first);
        // parameters/headers -> in order to avoid unnecessary fetches don't compare byref but term by term in the json
        switch (name) {
            case 'url':
            case 'method':
            case 'type':
            case 'as':
            case 'mode':
            case 'credentials':
            case 'disabled':
            case 'autofetch':
                this._delayAndConditionallyFetch();
                break;
            case 'parameters':
            case 'headers':
                if (!this.diffByValues
                    // spare round-trips: compare stringified values.
                    // state might be changed, by it's up to the dev to unlock this
                    // (i.e. just by calling 'fetch()' directly)
                    || Utils.jsonSortStringify(old) != Utils.jsonSortStringify(val)) {
                    this._delayAndConditionallyFetch();
                }
                break;
        }
    }
    _delayAndConditionallyFetch() {
        clearTimeout(this._handle);
        if (this.disabled) {
            this._abortPending();
        }
        else if (this.autofetch) {
            this._handle = setTimeout(() => {
                this.fetch();
            }, this.debounce || 0);
        }
    }
    _abortPending() {
        if (this.#aborter) {
            this.#aborter.abort(ABORT_MESSAGE);
            this.#aborter = null;
        }
    }
    #aborter;
    #ongoing;
    /** Returns a promise to a request having an already-consumed response body. */
    fetch() {
        if (this.throttle && this.fetching) {
            // exit
            return this.#ongoing;
        }
        return this.#ongoing = new Promise((resolve, reject) => {
            var url = this.url;
            // any reason to exit now?
            if (!this.isReady || Utils.isNullOrEmpty(url) || this.disabled) {
                resolve(null);
                return;
            }
            // go on...
            let _me = this;
            _me.fetching = true;
            const type = _me.type || 'json';
            let contentType = 'application/json';
            switch (type) {
                case 'raw':
                    contentType = 'application/x-www-form-urlencoded';
                    break;
            }
            const method = (_me.method || HttpMethod.Get).toUpperCase();
            let options = {
                headers: Utils.extend({ 'Content-Type': contentType }, _me.headers),
                method,
                mode: _me.mode || 'cors',
                credentials: _me.credentials || 'same-origin',
                cache: _me.cache || 'default'
            };
            const parameters = Utils.clone(_me.parameters ?? {});
            // complete a templated url, just in case (and remove params)
            // Note:    templated urls might sound pleonastic where you can simply concatenate pieces of string.
            //          They become handly in non-controllable autogenerated scenarios (e.g. autogenerated forms) where REST templated urls are involved.
            url = Utils.URIs.format(url, parameters, /* remove matched params if... */ method == "GET");
            if (!Utils.isNullOrEmpty(parameters)) {
                switch (method) {
                    case 'GET':
                        url = Utils.URIs.appendQuery(url, parameters);
                        break;
                    case 'PUT':
                    case 'POST':
                    case 'PATCH':
                        switch (_me.type) {
                            case 'raw':
                                let searchParams = new URLSearchParams();
                                for (let key in parameters) {
                                    searchParams.set(key, parameters[key]);
                                }
                                options.body = searchParams;
                                break;
                            default:
                                options.body = Utils.Json.stringify(parameters);
                                break;
                        }
                        break;
                }
            }
            // mandatory tmpl segments left over?
            if (Utils.URIs.hasMandatoryTemplateSegments(url)) {
                // exit
                resolve(null);
                return;
            }
            this._abortPending();
            const { signal } = this.#aborter = new AbortController();
            options.signal = signal;
            fetch(url, options).then(r => {
                this.#aborter = null;
                _me.fetching = false;
                if (r.ok) {
                    this.dispatchEvent(new CustomEvent(FetchSuccessEventName, { detail: r }));
                    const empty = Fetcher.isEmpty(r);
                    if (empty) {
                        switch (_me.as) {
                            case 'blob':
                            case 'image':
                                _me.result = null;
                                break;
                            case 'text':
                                _me.result = null;
                                break;
                            default:
                                _me.result = null;
                                break;
                        }
                        resolve(r);
                    }
                    else {
                        switch (_me.as) {
                            case 'blob':
                            case 'image':
                                r.blob().then(b => {
                                    if (_me.as === 'image') {
                                        Utils.blobToDataURL(b).then(i => {
                                            _me._acceptFetchResult(i);
                                        });
                                    }
                                    else {
                                        _me._acceptFetchResult(b);
                                    }
                                    resolve(r);
                                }, _ => {
                                    this.log(LogLevel.Warn, `Couldn't parse a ${_me.as}. ${_}`);
                                    resolve(null);
                                });
                                break;
                            case 'text':
                                r.text().then(t => {
                                    _me._acceptFetchResult(t);
                                    resolve(r);
                                });
                                break;
                            default:
                                r.json().then(j => {
                                    _me._acceptFetchResult(j);
                                    resolve(r);
                                }, _ => {
                                    this.log(LogLevel.Warn, `Couldn't parse a ${_me.as}. ${_}`);
                                    resolve(null);
                                });
                                break;
                        }
                    }
                }
                else {
                    this.dispatchEvent(new CustomEvent(FetchErrorEventName, { detail: r }));
                    reject(r);
                }
            }, (reason) => {
                if (typeof reason === 'string' && reason === ABORT_MESSAGE) {
                    this.log(LogLevel.Info, reason);
                    resolve(null);
                }
                else {
                    reject(reason);
                }
            });
        });
    }
    _acceptFetchResult(val) {
        this.dispatchEvent(new CustomEvent(FetchResultEventName, { detail: val }));
        this.result = val;
    }
};
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.String })
], PacemFetchElement.prototype, "url", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.String })
], PacemFetchElement.prototype, "method", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemFetchElement.prototype, "parameters", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.Json })
], PacemFetchElement.prototype, "headers", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFetchElement.prototype, "credentials", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFetchElement.prototype, "mode", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFetchElement.prototype, "cache", void 0);
__decorate([
    Watch({ converter: PropertyConverters.BooleanStrict })
], PacemFetchElement.prototype, "fetching", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.Boolean })
], PacemFetchElement.prototype, "autofetch", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFetchElement.prototype, "as", void 0);
__decorate([
    Watch({ emit: false, converter: PropertyConverters.String })
], PacemFetchElement.prototype, "type", void 0);
__decorate([
    Watch({ reflectBack: true, converter: PropertyConverters.Number })
], PacemFetchElement.prototype, "debounce", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.Number })
], PacemFetchElement.prototype, "throttle", void 0);
__decorate([
    Watch({ emit: false, reflectBack: true, converter: PropertyConverters.Boolean })
], PacemFetchElement.prototype, "diffByValues", void 0);
__decorate([
    Watch()
], PacemFetchElement.prototype, "result", void 0);
PacemFetchElement = __decorate([
    CustomElement({ tagName: P + '-fetch' })
], PacemFetchElement);
export { PacemFetchElement };
//}
